import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Package, Plus, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

export default function IngredientsPage() {
  const [ingredients, setIngredients] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stockDialog, setStockDialog] = useState(null);
  const [form, setForm] = useState({
    name_ko: '', name_ru: '', name_en: '', name_uz: '',
    unit: 'кг', current_stock: 0, min_stock: 0, cost_per_unit: 0, supplier: ''
  });
  const [stockForm, setStockForm] = useState({ quantity: 0, movement_type: 'purchase', notes: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    const r = await axios.get(`${API}/ingredients`);
    setIngredients(r.data);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/ingredients`, form);
      toast.success('Ингредиент добавлен');
      setDialogOpen(false);
      setForm({ name_ko: '', name_ru: '', name_en: '', name_uz: '', unit: 'кг', current_stock: 0, min_stock: 0, cost_per_unit: 0, supplier: '' });
      fetchData();
    } catch (err) { toast.error('Ошибка'); }
  };

  const handleStockMove = async () => {
    try {
      const qty = stockForm.movement_type === 'purchase' ? Math.abs(stockForm.quantity) : -Math.abs(stockForm.quantity);
      await axios.post(`${API}/stock-movements`, {
        ingredient_id: stockDialog.id,
        quantity: qty,
        movement_type: stockForm.movement_type,
        notes: stockForm.notes
      });
      toast.success('Изменения сохранены');
      setStockDialog(null);
      setStockForm({ quantity: 0, movement_type: 'purchase', notes: '' });
      fetchData();
    } catch (err) { toast.error('Ошибка'); }
  };

  return (
    <div className="space-y-6" data-testid="ingredients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Склад - Ингредиенты</h1>
          <p className="text-gray-500 mt-1">Учёт остатков сырья и материалов</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="cff-button-primary" data-testid="add-ingredient-btn">
              <Plus className="h-4 w-4 mr-2" /> Добавить
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Новый ингредиент</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Название (RU)</Label>
                  <Input value={form.name_ru} onChange={(e) => setForm({...form, name_ru: e.target.value})} placeholder="Мука" data-testid="ing-name-ru" />
                </div>
                <div>
                  <Label>Название (KO)</Label>
                  <Input value={form.name_ko} onChange={(e) => setForm({...form, name_ko: e.target.value})} placeholder="밀가루" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Ед. изм</Label>
                  <Input value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})} placeholder="кг" />
                </div>
                <div>
                  <Label>Остаток</Label>
                  <Input type="number" value={form.current_stock} onChange={(e) => setForm({...form, current_stock: parseFloat(e.target.value) || 0})} />
                </div>
                <div>
                  <Label>Мин. остаток</Label>
                  <Input type="number" value={form.min_stock} onChange={(e) => setForm({...form, min_stock: parseFloat(e.target.value) || 0})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Цена за ед. (₩)</Label>
                  <Input type="number" value={form.cost_per_unit} onChange={(e) => setForm({...form, cost_per_unit: parseFloat(e.target.value) || 0})} />
                </div>
                <div>
                  <Label>Поставщик</Label>
                  <Input value={form.supplier} onChange={(e) => setForm({...form, supplier: e.target.value})} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Отмена</Button>
              <Button onClick={handleCreate} className="cff-button-primary" data-testid="save-ing-btn">Сохранить</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Low stock alerts */}
      {ingredients.filter(i => i.current_stock <= i.min_stock).length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900">Низкие остатки!</p>
              <p className="text-sm text-amber-800">
                {ingredients.filter(i => i.current_stock <= i.min_stock).length} ингредиентов требуют пополнения
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ingredients.length === 0 ? (
          <Card className="p-12 text-center col-span-full">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Ингредиентов пока нет</p>
          </Card>
        ) : ingredients.map(ing => {
          const lowStock = ing.current_stock <= ing.min_stock;
          return (
            <Card key={ing.id} className={`p-5 cff-hover-lift ${lowStock ? 'border-amber-300 bg-amber-50' : ''}`} data-testid={`ing-card-${ing.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-gray-900">{ing.name_ru || ing.name_ko}</p>
                  <p className="text-xs text-gray-500">{ing.supplier || 'Без поставщика'}</p>
                </div>
                {lowStock && <AlertTriangle className="h-4 w-4 text-amber-600" />}
              </div>
              <div className="space-y-2 mb-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-gray-500">Остаток</span>
                  <span className="font-bold text-2xl cff-text-numeric">
                    {ing.current_stock} <span className="text-sm text-gray-500">{ing.unit}</span>
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Минимум:</span>
                  <span className="cff-text-numeric">{ing.min_stock} {ing.unit}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Цена:</span>
                  <span className="cff-text-numeric">₩{ing.cost_per_unit.toLocaleString()}/{ing.unit}</span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setStockDialog(ing)}
                data-testid={`move-stock-${ing.id}`}
              >
                Движение склада
              </Button>
            </Card>
          );
        })}
      </div>

      {/* Stock movement dialog */}
      <Dialog open={!!stockDialog} onOpenChange={(o) => !o && setStockDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{stockDialog?.name_ru || stockDialog?.name_ko}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Тип операции</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  onClick={() => setStockForm({...stockForm, movement_type: 'purchase'})}
                  className={`p-3 rounded-md border ${stockForm.movement_type === 'purchase' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200'}`}
                  data-testid="stock-purchase-btn"
                >
                  <ArrowUp className="h-4 w-4 mx-auto mb-1" />
                  <p className="text-xs font-medium">Поступление</p>
                </button>
                <button
                  onClick={() => setStockForm({...stockForm, movement_type: 'usage'})}
                  className={`p-3 rounded-md border ${stockForm.movement_type === 'usage' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200'}`}
                  data-testid="stock-usage-btn"
                >
                  <ArrowDown className="h-4 w-4 mx-auto mb-1" />
                  <p className="text-xs font-medium">Расход</p>
                </button>
              </div>
            </div>
            <div>
              <Label>Количество ({stockDialog?.unit})</Label>
              <Input
                type="number"
                value={stockForm.quantity}
                onChange={(e) => setStockForm({...stockForm, quantity: parseFloat(e.target.value) || 0})}
                data-testid="stock-qty-input"
              />
            </div>
            <div>
              <Label>Комментарий</Label>
              <Textarea
                value={stockForm.notes}
                onChange={(e) => setStockForm({...stockForm, notes: e.target.value})}
                placeholder="Например: закупка у поставщика..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockDialog(null)}>Отмена</Button>
            <Button onClick={handleStockMove} className="cff-button-primary" data-testid="save-stock-btn">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
